import { normalizeChannels, renderPlan, type OfflineContextFactory, type PlanRenderOptions, type RenderPlan } from '../audio/render';
import { lowerBound, sessionTimeline } from '../av/events';
import { sessionLoopTicks, sessionTiming } from '../av/sessionController';
import { foldPosition } from '../av/sessionClock';
import { secondsPerTick, secondsToTicks } from '../av/time';
import type { AvSession, Project } from '../domain/schemas';
import { requiredAudio } from '../music/compile';

/**
 * Offline session audio for video export (Phase 4B). Plays exactly what the live session
 * transport plays (`AudioEngine.startSession`) when started at the range start:
 *  - the session's music source (`sessionTimeline`: the song, or a pattern × bars);
 *  - the same loop range (`sessionLoopTicks`), unrolled pass by pass;
 *  - notes before the start position are not played; clips already sounding there join
 *    from the matching point (as `startSession` does); on later passes clips start at their
 *    tick and play their own length (as the scheduled Part does);
 *  - nothing is scheduled at or after the end; the last 5 ms fade out.
 * Output time 0 is the range start, the same origin as video frame 0.
 */

export const END_FADE_SEC = 0.005;

export interface SessionAudioPlan extends RenderPlan {
  wanted: { assetId: string; reversed: boolean }[];
  /** Loop passes the plan spans (1 without a loop). */
  passes: number;
}

/**
 * The schedule for `durationSec` seconds of session audio starting at session time
 * `startSec` (unwrapped; folded into the loop like the clock).
 */
export function sessionAudioPlan(project: Project, session: AvSession, startSec: number, durationSec: number): SessionAudioPlan {
  const bpm = project.settings.bpm;
  const spt = secondsPerTick(bpm);
  const timeline = sessionTimeline(project, session.music);
  const loop = sessionLoopTicks(project, session);
  const timing = sessionTiming(project, session);
  const startTick = secondsToTicks(foldPosition(startSec, timing.loop), bpm);
  const notes: RenderPlan['notes'] = [];
  const clips: RenderPlan['clips'] = [];

  // Segments of the transport's path: [fromTick, toTick) heard from output time `at`.
  const segments: { from: number; to: number; at: number }[] = [];
  if (!loop || startTick >= loop.endTicks) segments.push({ from: startTick, to: Infinity, at: 0 });
  else {
    let from = startTick;
    let at = 0;
    const len = (loop.endTicks - loop.startTicks) * spt;
    while (at < durationSec) {
      segments.push({ from, to: loop.endTicks, at });
      at += (loop.endTicks - from) * spt;
      from = loop.startTicks;
      if (len <= 0) break;
    }
  }

  for (const seg of segments) {
    const time = (tick: number) => seg.at + (tick - seg.from) * spt;
    // Both lists are sorted by tick (binary search keeps tiny loops over long exports cheap).
    for (let i = lowerBound(timeline.events, seg.from); i < timeline.events.length; i++) {
      const note = timeline.events[i]!;
      const t = time(note.tick);
      if (note.tick >= seg.to || t >= durationSec) break;
      notes.push({ note, time: t });
    }
    for (let i = lowerBound(timeline.clips, seg.from); i < timeline.clips.length; i++) {
      const clip = timeline.clips[i]!;
      const t = time(clip.tick);
      if (clip.tick >= seg.to || t >= durationSec) break;
      clips.push({ clip, time: t });
    }
  }
  // Clips already sounding at the start position join from the matching point (first pass only).
  for (const clip of timeline.clips) {
    const into = (startTick - clip.tick) * spt;
    if (into > 0.001 && into < clip.playSec) clips.push({ clip, time: 0, fromSec: into });
  }
  notes.sort((a, b) => a.time - b.time);
  clips.sort((a, b) => a.time - b.time);
  return { notes, clips, wanted: requiredAudio(project, timeline), passes: segments.length };
}

export interface SessionAudio {
  buffer: AudioBuffer;
  peak: number;
  normalizedDb: number;
  droppedNotes: number;
  renderMs: number;
  notes: number;
  clips: number;
}

/** Renders `frames` samples (at `sampleRate`) of session audio from `startSec`. */
export async function renderSessionAudio(
  project: Project,
  session: AvSession,
  startSec: number,
  frames: number,
  sampleRate: number,
  options: PlanRenderOptions & { createContext?: OfflineContextFactory } = {},
): Promise<SessionAudio> {
  const plan = sessionAudioPlan(project, session, startSec, frames / sampleRate);
  const result = await renderPlan(project, plan, plan.wanted, frames, options.createContext, sampleRate, options);
  const channels = Array.from({ length: result.buffer.numberOfChannels }, (_, i) => result.buffer.getChannelData(i));
  const { peak, normalizedDb } = normalizeChannels(channels);
  fadeOut(channels, Math.round(END_FADE_SEC * sampleRate));
  return { buffer: result.buffer, peak, normalizedDb, droppedNotes: result.droppedNotes, renderMs: result.renderMs, notes: plan.notes.length, clips: plan.clips.length };
}

/** Linear fade over the last `samples` samples (in place): voices cut at the end never click. */
export function fadeOut(channels: readonly Float32Array[], samples: number): void {
  for (const c of channels) {
    const n = Math.min(samples, c.length);
    for (let i = 0; i < n; i++) c[c.length - n + i]! *= (n - 1 - i) / n;
  }
}
